'use client';

import { motion } from 'framer-motion';
import { Play, Calendar, Film, HardDrive, Star } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

interface Movie {
  id: string;
  title: string;
  size: string;
  quality: string;
  year: string;
  format: string;
}

interface MovieListProps {
  movies: Movie[];
  onMovieSelect: (movie: Movie) => void;
  selectedMovie: Movie | null;
}

export default function MovieList({ movies, onMovieSelect, selectedMovie }: MovieListProps) {
  if (!movies || movies.length === 0) {
    return null;
  }

  return (
    <div className="mb-8">
      <motion.h2
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-2xl lg:text-3xl font-semibold mb-6 text-center"
      >
        Available Versions
      </motion.h2>
      
      <div className="grid gap-3 lg:gap-4 max-w-5xl mx-auto">
        {movies.map((movie, index) => (
          <motion.div
            key={movie.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: index * 0.05 }}
            whileHover={{ scale: 1.01 }}
            onClick={() => onMovieSelect(movie)}
            className={`glass-card p-4 lg:p-6 rounded-xl cursor-pointer transition-all duration-300 hover:border-red-600 ${
              selectedMovie?.id === movie.id
                ? 'border-red-600 ring-2 ring-red-600/50'
                : 'border-transparent'
            }`}
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              {/* Main content */}
              <div className="flex-1 space-y-3">
                {/* Title */}
                <h3 className="text-base lg:text-lg font-semibold text-white leading-tight">
                  {movie.title}
                </h3>

                {/* Badges */}
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-red-600/20 text-red-400 hover:bg-red-600/30 border-red-600/50">
                    <Star className="w-3 h-3 mr-1 fill-current" />
                    {movie.quality}
                  </Badge>
                  
                  <Badge className="bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 border-blue-600/50">
                    <HardDrive className="w-3 h-3 mr-1" />
                    {movie.size}
                  </Badge>
                  
                  <Badge className="bg-purple-600/20 text-purple-400 hover:bg-purple-600/30 border-purple-600/50">
                    <Film className="w-3 h-3 mr-1" />
                    {movie.format}
                  </Badge>
                  
                  <Badge className="bg-green-600/20 text-green-400 hover:bg-green-600/30 border-green-600/50">
                    <Calendar className="w-3 h-3 mr-1" />
                    {movie.year}
                  </Badge>
                </div>
              </div>

              {/* Action button */}
              <div className="flex-shrink-0">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white font-semibold gap-2 transition-transform hover:scale-105"
                >
                  <Play className="w-4 h-4 fill-current" />
                  <span className="hidden sm:inline">Stream Now</span>
                  <span className="sm:hidden">Play</span>
                </Button>
              </div>
            </div>

            {/* Selected indicator */}
            {selectedMovie?.id === movie.id && (
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: '100%' }}
                className="h-1 bg-gradient-to-r from-red-600 to-red-700 rounded-full mt-4"
              />
            )}
          </motion.div>
        ))}
      </div>

      {movies.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center text-gray-400 py-12"
        >
          <p className="text-lg">No versions found</p>
          <p className="text-sm mt-2">Try searching for a different movie</p>
        </motion.div>
      )}
    </div>
  );
}
